import React, { useState, useEffect } from 'react';
import {
  Row, Col, Form, Button, Card, Typography,
  Input, notification, Spin, Switch,
} from 'antd';
import { useNavigate, useMatch } from 'react-router-dom';
import { getUserPermissions, getUserType } from '../../../utils/users';
import {
  post, put, get, isSuperAgentError,
  RequestWrapper, ResponseWrapper,
} from '../../../dataSource/requests';
import './ManageCategory.scss';

interface CategoryFormOutput {
  name: string | undefined,
  active?: string,
}

const ManageCategory = (): React.ReactElement => {
  const navigate = useNavigate();
  const [formInstance] = Form.useForm();
  const routeMatchEdit = useMatch('/notificationcategories/:id/edit');
  const userPermissions = getUserPermissions();
  const userType = getUserType();

  const [mode] = useState(!routeMatchEdit ? 'CREATE' : 'EDIT');

  const [dataLoading, setDataLoading] = useState(false);

  useEffect(() => {
    const fetchCategoryData = async (categoryID: string): Promise<void> => {
      let response: ResponseWrapper | undefined;
      let responseError: ResponseWrapper | undefined;
      let genericError: Error | undefined;

      const requestReference = get({
        url: `/notificationCategory/findById/${categoryID}`,

        frontConfigs: {
          navigateFunction: navigate,
        },
      });

      try {
        setDataLoading(true);
        response = await requestReference;
      } catch (error: unknown) {
        console.log('error', error);

        if (isSuperAgentError(error)) {
          responseError = error.response;
        } else {
          genericError = error as Error;
        }
      }

      switch (true) {
        case (response?.body?.code === 18002):
          // "Notification category request successfully", 18002
          formInstance.setFieldsValue({
            name: response?.body?.notificationCategoryDetaisOutput?.name,
            active: response?.body?.notificationCategoryDetaisOutput?.active,
          });
          break;

        case (responseError?.body?.code === -4001):
          // "You must be authenticated to perform this operation", -4001
          break;

        case (responseError?.body?.code === -4003):
          // "You do not have permission to perform this operation", -4003
          notification.error({
            message: 'Erro',
            description: 'Você não tem permissão para realizar esta ação.',
          });
          navigate('/notificationcategories');
          break;

        case (responseError?.body?.code === -20000):
          // "Notification category not found", -20000
          notification.error({
            message: 'Erro',
            description: 'Categoria não encontrada',
          });
          navigate('/notificationcategories');
          break;

        case (genericError?.message?.includes('has been aborted')):
          // dont show anything when request is aborted
          break;

        default:
          notification.error({
            message: 'Erro',
            description: 'Desculpe, houve um erro ao realizar esta ação. Por favor tente novamente mais tarde.',
          });
          navigate('/notificationcategories');
          break;
      }

      setDataLoading(false);
    };

    const executeOnMount = async (): Promise<void> => {
      if (
        mode === 'EDIT'
        && routeMatchEdit?.params?.id != null
      ) {
        await fetchCategoryData(routeMatchEdit.params.id);
      }
    };

    executeOnMount();

    return () => {
      setDataLoading(false);
    };
  }, []);

  const handleSubmitCreate = async (values: CategoryFormOutput): Promise<void> => {
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    const requestCategory: RequestWrapper = post({
      url: '/notificationCategory/requestCategory',

      params: {
        name: values.name,
      },

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      setDataLoading(true);
      response = await requestCategory;
    } catch (error: unknown) {
      console.log('error', error);

      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (response?.body?.code === 18000):
        // "Notification category request successfully", 18000
        notification.success({
          message: 'Sucesso!',
          description: 'Categoria cadastrada.',
        });
        navigate('/notificationcategories');
        break;

      case (responseError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (responseError?.body?.code === -4003):
        // "You do not have permission to perform this operation", -4003
        notification.error({
          message: 'Erro',
          description: 'Você não possui permissão para realizar esta ação.',
        });
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao realizar esta ação. Por favor tente novamente mais tarde.',
        });
        break;
    }

    setDataLoading(false);
  };

  const handleSubmitEdit = async (values: CategoryFormOutput): Promise<void> => {
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    if (routeMatchEdit?.params?.id == null) {
      notification.error({
        message: 'Error',
        description: 'Desculpe, houve um erro ao realizar esta ação.',
      });

      navigate('/notificationcategories');
      return;
    }

    const requestReference = put({
      url: '/notificationCategory/editCategory',

      params: {
        id: routeMatchEdit.params.id,
        name: values.name,
        active: values.active,
      },

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      setDataLoading(true);
      response = await requestReference;
    } catch (error: unknown) {
      console.log('error', error);

      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (response?.body?.code === 18001):
        // "Notification category request successfully", 18001
        notification.success({
          message: 'Sucesso!',
          description: 'Categoria editada.',
        });
        navigate('/notificationcategories');
        break;

      case (responseError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (responseError?.body?.code === -4003):
        // "You do not have permission to perform this operation", -4003
        notification.error({
          message: 'Erro',
          description: 'Você não possui permissão para realizar esta ação.',
        });
        break;

      case (responseError?.body?.code === -20000):
        // "Notification category not found", -20000
        notification.error({
          message: 'Erro',
          description: 'Categoria não encontrada.',
        });
        navigate('/notificationcategories');
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao realizar esta ação. Por favor tente novamente mais tarde.',
        });
        break;
    }
    setDataLoading(false);
  };

  return (
    <div
      className="ManageCategory"
    >
      <Row
        className="ManageCategory__ComponentRow"
        gutter={[24, 24]}
      >
        <Col
          className="ManageCategory__ComponentColumn"
          xs={{ span: 24 }}
        >
          <Row
            gutter={[24, 24]}
            justify="space-between"
            className="ManageCategory__PageTitleRow"
          >
            <Col
              className="ManageCategory__PageTitleColumn"
            >
              <Typography.Title
                className="ManageCategory__PageTitle"
              >
                {
                  mode === 'CREATE'
                    ? 'Cadastrar categoria'
                    : 'Editar categoria'
                }
              </Typography.Title>
            </Col>

            <Col
              className="ManageCategory__ButtonColumn"
              xs={{ span: 24 }}
              sm={{ span: 24 }}
              md={{ span: 6 }}
              lg={{ span: 4 }}
              xl={{ span: 4 }}
            >
              <Row
                gutter={[24, 24]}
                justify="end"
                className="ManageCategory__NestedButtonRow"
              >
                <Col
                  xs={{ span: 24 }}
                  className="ManageCategory__BackButtonColumn"
                >
                  <Button
                    block
                    type="default"
                    href="/notificationcategories"
                    className="ManageCategory__BackButton"
                    onClick={
                      (eventt): void => {
                        eventt.preventDefault();
                        navigate('/notificationcategories');
                      }
                    }
                  >
                    Voltar
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>

          <Row
            gutter={[24, 24]}
            className="ManageCategory__ContentRow"
          >
            <Col
              className="ManageCategory__ContentColumn"
              xs={{ span: 24 }}
            >
              <Card
                className="ManageCategory__Card"
              >
                <Spin
                  spinning={dataLoading}
                  tip="Carregando..."
                  className="ManageCategory__Spin"
                >
                  <Form
                    layout="vertical"
                    className="ManageCategory__Form"
                    form={formInstance}
                    initialValues={{
                      active: true,
                    }}
                    onFinish={
                      async (formValues): Promise<void> => {
                        if (mode === 'CREATE') {
                          handleSubmitCreate(formValues);
                        } else {
                          handleSubmitEdit(formValues);
                        }
                      }
                    }
                  >
                    <Row
                      gutter={[24, 24]}
                      className="ManageCategory__NameFieldRow"
                    >
                      <Col
                        xs={{ span: 24 }}
                        sm={{ span: 24 }}
                        md={{ span: 12 }}
                        lg={{ span: 12 }}
                        className="ManageCategory__NameFieldColumn"
                      >
                        <Form.Item
                          className="ManageCategory__NameFormItem"
                          label={(
                            <Typography.Text
                              className="ManageCategory__AllFormLabels ManageCategory__NameFormLabel"
                            >
                              Nome
                            </Typography.Text>
                          )}
                          name="name"
                          rules={[
                            {
                              required: true,
                              message: 'Nome é obrigatório.',
                            },
                            {
                              min: 3,
                              message: 'Nome deve ter no mínimo 3 caracteres.',
                            },
                            {
                              max: 50,
                              message: 'Nome deve ter no máximo 50 caracteres.',
                            },
                          ]}
                        >
                          <Input className="ManageCategory__NameInput" />
                        </Form.Item>
                      </Col>

                      {
                        userType === 'COOPERCITRUS'
                          && (userPermissions || []).includes('Inativação de categoria')
                          && mode === 'EDIT'
                          ? (
                            <Col
                              xs={{ span: 24 }}
                              sm={{ span: 24 }}
                              md={{ span: 12 }}
                              lg={{ span: 12 }}
                              className="ManageCategory__StatusColumn"
                            >
                              <Form.Item
                                className="ManageCategory__StatusFormItem"
                                label={(
                                  <Typography.Text
                                    className="ManageCategory__AllFormLabels ManageCategory__StatusLabel"
                                  >
                                    Status
                                  </Typography.Text>
                                )}
                                name="active"
                                rules={[]}
                                valuePropName="checked"
                              >
                                <Switch
                                  className="ManageCategory__StatusInput"
                                  size="default"
                                  unCheckedChildren="Inativo"
                                  checkedChildren="Ativo"
                                />
                              </Form.Item>
                            </Col>
                          )
                          : null
                      }
                    </Row>

                    <Row
                      justify="end"
                      gutter={[24, 24]}
                      className="ManageCategory__ButtonsRow"
                    >
                      <Col
                        xs={{ span: 24 }}
                        sm={{ span: 12 }}
                        md={{ span: 6 }}
                        className="ManageCategory__SubmitButtonColumn"
                      >
                        <Form.Item
                          className="ManageCategory__SubmitButtonFormItem"
                        >
                          <Button
                            block
                            type="primary"
                            htmlType="submit"
                            className="ManageCategory__SubmitButton"
                          >
                            Salvar
                          </Button>
                        </Form.Item>
                      </Col>
                    </Row>
                  </Form>
                </Spin>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>
    </div>
  );
};

ManageCategory.propTypes = {};
ManageCategory.defaultProps = {};

export default ManageCategory;
